import { useAppTranslation } from "@/utils";
import { useInfiniteScroll } from "@/utils/use-infinite-scroll";
import { bnplConsumerApis } from "@payping/api";
import { OrderStatusRequest } from "@payping/api/app/api/bnplConsumerTypes";
import type { OrderListResponse } from "@payping/api/app/api/bnplConsumerTypes";
import { Empty, Flex, Spin, Tabs, useMessagedAsyncFn } from "@payping/web-components";
import { useEffect, useRef, useState } from "react";
import PurchaseHistoryOrderItem from "./OrderItem";

const TABS = {
  [OrderStatusRequest.Canceled]: String(OrderStatusRequest.Canceled),
  [OrderStatusRequest.NotPaidOff]: String(OrderStatusRequest.NotPaidOff),
  [OrderStatusRequest.PaidOff]: String(OrderStatusRequest.PaidOff),
} as const;

type TABS = (typeof TABS)[keyof typeof TABS];
const PurchaseHistoryPage = () => {
  const { t } = useAppTranslation();
  const [currenTab, setCurrentTab] = useState<TABS>(String(TABS[OrderStatusRequest.NotPaidOff]));

  const [pageNumber, setPageNumber] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const [ordersList, setOrdersList] = useState<OrderListResponse>();

  const [{ loading }, getList] = useMessagedAsyncFn(
    async (pgNumber: number, orderStatusRequest: OrderStatusRequest) => {
      const res = await bnplConsumerApis.dashboardOrderList(
        undefined,
        undefined,
        orderStatusRequest,
        undefined,
        10,
        pgNumber,
      );
      setPageNumber(pgNumber);
      setOrdersList((pre) => ({ ...pre, orders: [...(pre?.orders ?? []), ...res.orders], total: (res as any).total }));
      return res;
    },

    [setPageNumber],
    { loading: false },
    {
      success: false,
      error: true,
    },
  );

  // only first time
  useEffect(() => {
    const newPageNumber = pageNumber + 1;
    getList(newPageNumber, parseInt(currenTab, 10) as OrderStatusRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectDependency = [loading, ordersList, pageNumber, currenTab] as const;

  useInfiniteScroll<HTMLDivElement, HTMLDivElement, typeof effectDependency>({
    triggerCallback({ effectDependency: eDependency }) {
      const load = eDependency[0];
      const Ilist = eDependency[1];
      const pgN = eDependency[2];
      const currTab = parseInt(eDependency[3], 10);

      if (!load && (Ilist?.orders ?? []).length < (Ilist?.total ?? 0)) {
        const newPageNumber = pgN + 1;
        getList(newPageNumber, currTab as OrderStatusRequest);
      }
    },
    effectDependency,
    scrollTrigger: scrollDivRef,
    scrollWrapper: containerRef,
  });

  const renderEmptyDescription = () => {
    if (currenTab === String(TABS[OrderStatusRequest.NotPaidOff])) {
      return t("home.purchaseHistory.notPaidOffEmpty");
    }
    if (currenTab === String(TABS[OrderStatusRequest.Canceled])) {
      return t("home.purchaseHistory.canceledEmpty");
    }
    if (currenTab === String(TABS[OrderStatusRequest.PaidOff])) {
      return t("home.purchaseHistory.paidOffEmpty");
    }
    return "";
  };

  return (
    <div ref={containerRef}>
      <Flex className="tw:w-full tw:p-3!" gap="middle" vertical>
        <Tabs
          items={[
            {
              key: String(TABS[OrderStatusRequest.NotPaidOff]),
              label: t("home.purchaseHistory.tabs.notPaidOff"),
            },
            {
              key: String(TABS[OrderStatusRequest.PaidOff]),
              label: t("home.purchaseHistory.tabs.paidOff"),
            },
            {
              key: String(TABS[OrderStatusRequest.Canceled]),
              label: t("home.purchaseHistory.tabs.canceled"),
            },
          ]}
          onChange={(key) => {
            setCurrentTab(key);
            setPageNumber(1);
            setOrdersList(undefined);
            getList(1, parseInt(key, 10) as OrderStatusRequest);
          }}
          defaultActiveKey={String(currenTab)}
        />
        {!loading && !ordersList?.orders.length ? (
          <Empty description={renderEmptyDescription()} className="tw:mt-12!" />
        ) : null}
        {ordersList?.orders.map((item) => (
          <PurchaseHistoryOrderItem key={item.trackingCode} data={item} />
        ))}
      </Flex>
      <Flex vertical className="tw:w-full">
        <Flex className="w-100" justify="center" align="center" vertical>
          {loading ? <Spin /> : null}
          <div ref={scrollDivRef} className="w-100" style={{ height: "1px" }}></div>
        </Flex>
      </Flex>
    </div>
  );
};

export default PurchaseHistoryPage;
